import { AppConfigProvider } from '@root/app/app.config.provider';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Scenario } from '@models/scenario';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs';
import { tap, debounceTime, throttleTime, catchError, defaultIfEmpty, finalize } from 'rxjs/operators';
import { backoff } from '@shared/helpers/backoff';

export enum ScenarioAction {
  Idle,
  Created,
  Updated,
  Deleted,
  Undeleted,
  Fetch
}

@Injectable({ providedIn: 'root' })
export class ScenarioService {
  private scenariosUrl;
  private lastFetch: any;
  private _scenarioList;
  public scenarioAction$: BehaviorSubject<ScenarioAction> = new BehaviorSubject<ScenarioAction>(ScenarioAction.Idle);
  public scenarioList$: BehaviorSubject<Scenario[]> = new BehaviorSubject<Scenario[]>([]);

  public get scenarioList() {
    return this._scenarioList;
  }
  public set scenarioList(value) {
    this._scenarioList = value;
  }

  constructor(private http: HttpClient, private appConfig: AppConfigProvider) {
    this.lastFetch = 0;
    this.appConfig.environment.subscribe(env => {
      this.scenariosUrl = `${env.API.SCENARIOAPI}/api/scenarios`;
    });
  }

  /**
   * Get the list of scenarios from the database. Loads the observable scenarioList$ with this information.
   * @returns An array of Scenarios or empty array
   */
  getScenarios(): Observable<any> {
    const dateDiff = Date.now() - this.lastFetch;
    // if (dateDiff < 30000 && this.scenarioList) {
    //   return of(this.scenarioList);
    // }
    const scenarios$ = this.http.get<Scenario[]>(this.scenariosUrl).pipe(
      tap(scenarios => {
        this.lastFetch = Date.now();
        this.scenarioList = scenarios;
        this.scenarioList$.next(scenarios);
        this.scenarioAction$.next(ScenarioAction.Fetch);
      }),
      debounceTime(250),
      throttleTime(250),
      backoff(3, 250),
      defaultIfEmpty([])
    );
    return scenarios$;
  }

  /**
   * Adds an scenario by ID
   * @param scenarioId Scenario ID that will be deleted
   * @returns The new scenario element if successful
   */
  addScenario(scenario: Scenario) {
    if (!scenario.scenario_description) {
      scenario.scenario_description = '';
    }
    const newScenario = {
      ...scenario,
      user_id: -1,
      form_id: 'Scenario Admin Page'
    };
    this.lastFetch = 0;
    return this.http.post(this.scenariosUrl, newScenario).pipe(
      tap(res => {
        this.scenarioList$.next([...this.scenarioList, res]);
        this.scenarioAction$.next(ScenarioAction.Created);
      }),
      catchError(err => {
        console.error('Problem trying to add Scenario', err);
        return err;
      })
    );
  }

  copyScenario(scenario: Scenario) {
    if (!scenario.scenario_description) {
      scenario.scenario_description = '';
    }
    const copiedScenario = {
      ...scenario,
      scenarioId:null,
      user_id: -1,
      form_id: 'Scenario Admin Page'
    };
    this.lastFetch = 0;
    return this.http.post(`${this.scenariosUrl}/copy/${scenario.scenario_id}`, copiedScenario).pipe(
      tap(res => {
        this.scenarioList$.next([...this.scenarioList, res]);
        this.scenarioAction$.next(ScenarioAction.Created);
      }),
      catchError(err => {
        console.error('Problem trying to add Scenario', err);
        return err;
      })
    );
  }

  /**
   * Updates an scenario by ID
   * @param scenarioId Scenario ID that will be deleted
   * @returns The updated scenario element if successful
   */
  updateScenario(scenario: Scenario) {
    const updatedScenario = {
      ...scenario,
      user_id: -1,
      form_id: 'Scenario Admin Page'
    };
    this.lastFetch = 0;
    return this.http.post(`${this.scenariosUrl}/${scenario.scenario_id}`, updatedScenario).pipe(
      catchError(err => {
        console.error('Problem trying to update Scenario', err);
        return err;
      }),
      tap((res: Scenario) => {
        const foundIndex = this.scenarioList.findIndex((scen: Scenario) => scen.scenario_id === res.scenario_id);
        if (foundIndex) {
          this.scenarioList[foundIndex] = res;
          this.scenarioList$.next(this.scenarioList);
          this.scenarioAction$.next(ScenarioAction.Updated);
        }
      })
    ); // returns updated scenario
  }

  /**
   * Deletes an scenario by ID
   * @param scenarioId Scenario ID that will be deleted
   * @returns The deleted scenario element if successful
   */
  deleteScenario(scenarioId: number) {
    const userId = -1;

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: { userId, scenarioId }
    };
    this.lastFetch = 0;
    return this.http.delete(`${this.scenariosUrl}`, options).pipe(
      catchError(err => {
        console.error('Problem trying to delete Scenario', err);

        return err;
      }),
      tap((res: Scenario) => {
        const newScenarioList = this.scenarioList.filter((scen: Scenario) => scen.scenario_id !== res.scenario_id);
        this.scenarioList$.next(newScenarioList);
        this.scenarioAction$.next(ScenarioAction.Deleted);
      })
    );
  }

  /**
   * Undeletes an scenario by ID
   * @param scenarioId Scenario ID that will be deleted
   * @returns The undeleted scenario element if successful
   */
  undeleteScenario(scenarioId: number) {
    const userId = -1;
    this.lastFetch = 0;
    return this.http.patch(`${this.scenariosUrl}`, { userId, scenarioId }).pipe(
      catchError(err => {
        console.error('Problem trying to undelete Scenario', err);
        return err;
      }),
      tap(res => {
        this.scenarioList$.next([...this.scenarioList, res]);
        this.scenarioAction$.next(ScenarioAction.Undeleted);
      }) // returns undeleted scenario
    );
  }
}
